import React, {Component} from 'react';
import {connect} from 'react-redux';
import Drawer from '@material-ui/core/Drawer';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {loadImpact, loadDdl} from '../dependency-analysis.actions';

const CHANGE_TYPES = [
  {value: 'drop_table', label: 'Drop table'},
  {value: 'drop_column', label: 'Drop column'},
  {value: 'alter_column_type', label: 'Alter column type'},
  {value: 'rename', label: 'Rename'},
  {value: 'drop_view', label: 'Drop view'},
];

const DIALECTS = [
  {value: 'mysql', label: 'MySQL'},
  {value: 'mssql', label: 'SQL Server'},
  {value: 'postgres', label: 'PostgreSQL'},
  {value: 'oracle', label: 'Oracle'},
];

const COLUMN_CHANGE_TYPES = new Set(['drop_column', 'alter_column_type']);

class RiImpactPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      objectId: props.initialObjectId || '',
      changeType: 'drop_table',
      columnId: '',
      dialect: 'mysql',
    };
    this.handleObjectChange = this.handleObjectChange.bind(this);
    this.handleChangeTypeChange = this.handleChangeTypeChange.bind(this);
    this.handleColumnChange = this.handleColumnChange.bind(this);
    this.handleDialectChange = this.handleDialectChange.bind(this);
    this.handleCopyDdl = this.handleCopyDdl.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.initialObjectId !== this.props.initialObjectId && this.props.initialObjectId) {
      this.setState({objectId: this.props.initialObjectId}, this.fetchData);
    }
    if (!prevProps.open && this.props.open && this.state.objectId) {
      this.fetchData();
    }
  }

  fetchData() {
    const {objectId, changeType, columnId, dialect} = this.state;
    const {schemaId} = this.props;
    if (!objectId) return;
    this.props.loadImpact({schemaId, objectId, changeType, columnId: COLUMN_CHANGE_TYPES.has(changeType) ? columnId : undefined});
    this.props.loadDdl({schemaId, objectId, changeType, columnId: COLUMN_CHANGE_TYPES.has(changeType) ? columnId : undefined, dialect});
  }

  handleObjectChange(e) {
    this.setState({objectId: e.target.value, columnId: ''}, this.fetchData);
  }

  handleChangeTypeChange(e) {
    this.setState({changeType: e.target.value, columnId: ''}, this.fetchData);
  }

  handleColumnChange(e) {
    this.setState({columnId: e.target.value}, this.fetchData);
  }

  handleDialectChange(e) {
    this.setState({dialect: e.target.value}, () => {
      const {objectId, changeType, columnId, dialect} = this.state;
      const {schemaId} = this.props;
      if (!objectId) return;
      this.props.loadDdl({schemaId, objectId, changeType, columnId: COLUMN_CHANGE_TYPES.has(changeType) ? columnId : undefined, dialect});
    });
  }

  handleCopyDdl() {
    const {ddl} = this.props;
    if (!ddl || !ddl.ddl) return;
    navigator.clipboard.writeText(ddl.ddl).catch(() => {});
  }

  getAvailableColumns() {
    const {graph, columnsByObject} = this.props;
    const {objectId} = this.state;
    if (!objectId) return [];
    if (columnsByObject && columnsByObject[objectId]) return columnsByObject[objectId];
    if (!graph || !graph.nodes) return [];
    const node = graph.nodes.find((n) => (n.objectId || n.id) === objectId);
    return (node && node.columns) || [];
  }

  getObjectOptions() {
    const {graph} = this.props;
    if (!graph || !graph.nodes) return [];
    return graph.nodes.map((n) => ({id: n.objectId || n.id, name: n.objectName || n.label}));
  }

  render() {
    const {open, onClose, impact, ddl, isLoading} = this.props;
    const {objectId, changeType, columnId, dialect} = this.state;
    const showColumnPicker = COLUMN_CHANGE_TYPES.has(changeType);
    const cols = this.getAvailableColumns();
    const objects = this.getObjectOptions();
    const impactLoading = isLoading.includes('loadImpact');
    const ddlLoading = isLoading.includes('loadDdl');
    const ddlText = ddl && ddl.ddl ? ddl.ddl : '';

    return (
      <Drawer anchor="right" open={open} onClose={onClose} className="ri-impact-drawer">
        <h3 className="ri-impact-title">Impact Analysis</h3>

        <div className="ri-impact-controls">
          <div className="ri-impact-control-group">
            <span className="ri-impact-label">Object</span>
            <Select
              value={objectId}
              onChange={this.handleObjectChange}
              displayEmpty
              inputProps={{'data-testid': 'ri-impact-object'}}
            >
              <MenuItem value=""><em>Select object…</em></MenuItem>
              {objects.map((o) => (
                <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
              ))}
            </Select>
          </div>

          <div className="ri-impact-control-group">
            <span className="ri-impact-label">Change type</span>
            <Select
              value={changeType}
              onChange={this.handleChangeTypeChange}
              inputProps={{'data-testid': 'ri-impact-changetype'}}
            >
              {CHANGE_TYPES.map((ct) => (
                <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>
              ))}
            </Select>
          </div>

          {showColumnPicker && (
            <div className="ri-impact-control-group">
              <span className="ri-impact-label">Column</span>
              <Select
                value={columnId}
                onChange={this.handleColumnChange}
                displayEmpty
                inputProps={{'data-testid': 'ri-impact-column'}}
              >
                <MenuItem value=""><em>Select column…</em></MenuItem>
                {cols.map((c) => (
                  <MenuItem key={c.columnId || c.name} value={c.columnId || c.name}>
                    {c.name || c.columnName}
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}

          <div className="ri-impact-control-group">
            <span className="ri-impact-label">Dialect</span>
            <Select
              value={dialect}
              onChange={this.handleDialectChange}
              inputProps={{'data-testid': 'ri-impact-dialect'}}
            >
              {DIALECTS.map((d) => (
                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {impactLoading && <div style={{color: '#606060', fontSize: '1.3rem'}}>Loading impact…</div>}

        {!impactLoading && impact && (
          <div>
            {impact.upstreamReferences && impact.upstreamReferences.length > 0 && (
              <div className="ri-impact-section">
                <div className="ri-impact-section-title">Upstream references to check first</div>
                <div className="ri-impact-chips">
                  {impact.upstreamReferences.map((ref) => {
                    const hasCr = ref.badges && ref.badges.includes('OPEN_CR');
                    return (
                      <span
                        key={ref.objectId}
                        className={`ri-impact-chip ${hasCr ? 'ri-chip-open-cr' : ''}`}
                        title={hasCr ? 'Has an open change request' : undefined}
                      >
                        {ref.objectName}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {impact.downstreamImpacted && impact.downstreamImpacted.length > 0 && (
              <div className="ri-impact-section">
                <div className="ri-impact-section-title">Downstream objects impacted</div>
                <div className="ri-impact-chips">
                  {impact.downstreamImpacted.map((obj) => (
                    <span key={obj.objectId} className="ri-impact-chip">{obj.objectName}</span>
                  ))}
                </div>
              </div>
            )}

            {impact.handleOrder && impact.handleOrder.length > 0 && (
              <div className="ri-impact-section">
                <div className="ri-impact-section-title">Handle-first order</div>
                <ol className="ri-impact-order-list">
                  {impact.handleOrder.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {!impactLoading && impact && (
          <div className="ri-impact-section">
            <div className="ri-impact-section-title">Suggested DDL</div>
            {ddl && ddl.oracleNote && (
              <div className="ri-oracle-note">{ddl.oracleNote}</div>
            )}
            {ddlLoading && <div style={{color: '#606060', fontSize: '1.3rem'}}>Loading DDL…</div>}
            {!ddlLoading && ddlText && (
              <div>
                <pre className="ri-impact-ddl-block" data-testid="ri-impact-ddl">{ddlText}</pre>
                <div className="ri-ddl-copy-row">
                  <button
                    type="button"
                    className="btn btn-default"
                    data-testid="ri-impact-copy-ddl"
                    onClick={this.handleCopyDdl}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
            {!ddlLoading && !ddlText && (
              <span style={{color: '#606060', fontSize: '1.2rem', fontStyle: 'italic'}}>No DDL generated.</span>
            )}
          </div>
        )}
      </Drawer>
    );
  }
}

const mapStateToProps = (state) => ({
  impact: state.dependencyAnalysis.impact,
  ddl: state.dependencyAnalysis.ddl,
  graph: state.dependencyAnalysis.graph,
  columnsByObject: state.dependencyAnalysis.columnsByObject,
  isLoading: state.dependencyAnalysis.isLoading,
});

const mapDispatchToProps = {loadImpact, loadDdl};

export default connect(mapStateToProps, mapDispatchToProps)(RiImpactPanel);
